import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { ShipmentModel, UnitModel } from '@data-layer/models/common';
import { EntrySummaryService } from '@services/entry-summary.service';
import { Observable, of, ReplaySubject } from 'rxjs';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { enumChargesDistributionType, enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { EntrySummaryStateService, EsCreateBlFormService, EsLabelsService, EsStepsService } from '@shared/es-mutual/services';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { DocumentsService } from '@services/documents.service';
import { CompaniesService } from '@services/companies.service';
import { CompanyModel } from '@data-layer/models/companies';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { EntityModel } from '@data-layer/models/entities';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { IBillOfLadingFormData } from '@app/modules/entry-summary/interfaces';
import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { CodeRepository } from '@data-layer/repositories';
import { EntityValidationHelper } from '@helpers/entity-validation.helper';
import { EsEntityUnifyDefaultDesktopComponent } from '../es-entity-unify-default-desktop/es-entity-unify-default-desktop.component';
import { DESC_ENTITY } from '@constants/desc-entity';
import { EsEditShipmentDesktopComponent } from '../es-edit-shipment-desktop/es-edit-shipment-desktop.component';
import { enumIdentifierQualifier } from '@app/data-layer/enums/common';
import { AppService } from '@app/app.service';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';

@Component({
  selector: 'es-bill-of-lading-edit-desktop',
  template: `
    <es-shipment-summary-desktop
      *ngIf="isShowHeader"
      [entrySummary]="entrySummary"
      [company]="company"
      [isCanEdit]="isCanEdit"
      [isShowFirstLine]="false"
      [isBodyOpen]="false"
      [isShowType]="false"
      (editDetails)="onEditDetails('isMainInfo')"
      (editFrom)="onEditDetails('isFromInfo')"
      (editTo)="onEditDetails('isToInfo')"
      (editVia)="onEditDetails('isViaInfo')"
    ></es-shipment-summary-desktop>

    <section class="es-editable-bl">
      <header class="es-editable-bl-header">
        <div class="d-flex align-items-center">
          <h3>{{ 'DOCUMENT_TYPE.BILL_OF_LADING' | translate }}</h3>
        </div>

        <div class="es-editable-bl-header-actions">
          <button type="button" class="btn btn-link btn-arrow ml-2" (click)="isBodyOpen = !isBodyOpen">
            <i class="fa" [ngClass]="{ 'fa-chevron-up': isBodyOpen, 'fa-chevron-down': !isBodyOpen }"></i>
          </button>
        </div>
      </header>
      <div class="es-editable-bl-body is-editable" *ngIf="isBodyOpen">
        <p class="caption-hint" *ngIf="!isRail" [innerHTML]="'ENTRY_SUMMARY.SCAC_HINT' | translate"></p>
        <p class="caption-hint" *ngIf="isRail" [innerHTML]="'INFO.BL.SUB_TITLE_RAIL' | translate"></p>

        <section class="es-editable-bl-body-details" [ngClass]="{ 'is-valid': isDetailsValid }">
          <header class="es-editable-bl-body-details-header">
            <div class="d-flex align-items-center justify-content-start">
              <div class="d-flex align-items-center">
                <span *ngIf="isDetailsValid">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M10.0757 16.7728L6.17573 12.8728C5.94142 12.6385 5.94142 12.2586 6.17573 12.0243L7.02424 11.1757C7.25854 10.9414 7.63846 10.9414 7.87277 11.1757L10.5 13.8029L16.1272 8.17573C16.3615 7.94142 16.7415 7.94142 16.9758 8.17573L17.8243 9.02426C18.0586 9.25857 18.0586 9.63846 17.8243 9.87279L10.9243 16.7728C10.6899 17.0071 10.31 17.0071 10.0757 16.7728Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>

                <h5>
                  {{ 'LABELS.BILL_OF_LADING_DETAILS' | translate }}
                </h5>
              </div>

              <app-hint-tooltip-icon [hint]="'LABELS.BILL_OF_LADING_DETAILS' | translate"></app-hint-tooltip-icon>
            </div>
          </header>

          <div class="es-editable-bl-body-details-body">
            <div class="es-editable-bl-body-details-body-icon">
              <img src="assets/icons/bond.svg" [alt]="'LABELS.BILL_OF_LADING_DETAILS' | translate" />
            </div>
            <div class="es-editable-bl-body-details-body-form" [ngClass]="{ 'no-border': isHasEsOcrRedesign }">
              <p class="caption-hint" *ngIf="!isRail" [innerHTML]="'ENTRY_SUMMARY.SCAC_HINT' | translate"></p>
              <p class="caption-hint" *ngIf="isRail" [innerHTML]="'INFO.BL.SUB_TITLE_RAIL' | translate"></p>

              <div class="form-row">
                <div class="col-6">
                  <app-control-wrapper
                    [control]="formGroup.get(['masterBill', 'carrier'])"
                    [errors]="errors.masterBillCarrier"
                    [helper]="billOfLadingData.carrier.helper"
                  >
                    <label for="masterBillNumber" control-label>
                      {{ billOfLadingData.carrier.titleMaster | translate }}

                      <app-hint-tooltip-icon [hint]="billOfLadingData.carrier.tooltip | translate"></app-hint-tooltip-icon>
                    </label>
                    <input
                      type="text"
                      [formControl]="formGroup.get(['masterBill', 'carrier'])"
                      class="form-control"
                      id="masterBillCarrier"
                      name="masterBillCarrier"
                      [mask]="billOfLadingData.carrier.maskMaster"
                      [attr.inputmode]="billOfLadingData.carrier.inputmodeMaster"
                      [attr.pattern]="billOfLadingData.carrier.patternMaster"
                      [appMaskErrorTooltip]="billOfLadingData.carrier.maskMessageMaster"
                      [maxMaskLength]="billOfLadingData.carrier.maxMaskLength"
                      control-wrapper
                    />
                  </app-control-wrapper>
                </div>
                <div class="col-6">
                  <app-control-wrapper
                    [control]="formGroup.get(['masterBill', 'number'])"
                    [errors]="errors.masterBillNumber"
                    [helper]="billOfLadingData.number.helper"
                  >
                    <label for="masterBillNumber" control-label>
                      {{ billOfLadingData.number.titleMaster | translate }}
                      <span class="ml-1 mr-1" *ngIf="isTruck">({{ 'LABELS.OPTIONAL' | translate }})</span>

                      <app-hint-tooltip-icon [hint]="billOfLadingData.number.tooltip | translate"></app-hint-tooltip-icon>
                    </label>
                    <input
                      type="text"
                      [formControl]="formGroup.get(['masterBill', 'number'])"
                      class="form-control"
                      id="masterBillNumber"
                      name="masterBillNumber"
                      [mask]="billOfLadingData.number.maskMaster"
                      [attr.inputmode]="billOfLadingData.number.inputmodeMaster"
                      [attr.pattern]="billOfLadingData.number.patternMaster"
                      [appMaskErrorTooltip]="billOfLadingData.number.maskMessageMaster"
                      [maxMaskLength]="billOfLadingData.number.maxMaskLength"
                      control-wrapper
                    />
                  </app-control-wrapper>
                </div>
              </div>

              <div class="form-row" *ngIf="!isRail && !isTruck">
                <div class="col-6">
                  <app-control-wrapper
                    [control]="formGroup.get(['houseBill', 'carrier'])"
                    [errors]="errors.houseBillCarrier"
                    [helper]="billOfLadingData.carrier.helper"
                  >
                    <label for="houseBillCarrier" control-label>
                      {{ billOfLadingData.carrier.titleHouse | translate }}

                      <app-hint-tooltip-icon [hint]="billOfLadingData.carrier.tooltip | translate"></app-hint-tooltip-icon>
                    </label>
                    <input
                      type="text"
                      [formControl]="formGroup.get(['houseBill', 'carrier'])"
                      class="form-control"
                      id="houseBillCarrier"
                      name="houseBillCarrier"
                      [mask]="billOfLadingData.carrier.maskHouse"
                      [validation]="false"
                      [attr.inputmode]="billOfLadingData.carrier.inputmodeHouse"
                      [attr.pattern]="billOfLadingData.carrier.patternHouse"
                      [appMaskErrorTooltip]="billOfLadingData.carrier.maskMessageHouse"
                      [maxMaskLength]="billOfLadingData.carrier.maxMaskLength"
                      control-wrapper
                    />
                  </app-control-wrapper>
                </div>
                <div class="col-6">
                  <app-control-wrapper
                    [control]="formGroup.get(['houseBill', 'number'])"
                    [errors]="errors.houseBillNumber"
                    [helper]="billOfLadingData.number.helper"
                  >
                    <label for="houseBillNumber" control-label>
                      {{ billOfLadingData.number.titleHouse | translate }}

                      <app-hint-tooltip-icon [hint]="billOfLadingData.number.tooltip | translate"></app-hint-tooltip-icon>
                    </label>
                    <input
                      type="text"
                      [formControl]="formGroup.get(['houseBill', 'number'])"
                      class="form-control"
                      id="houseBillNumber"
                      name="houseBillNumber"
                      [mask]="billOfLadingData.number.maskHouse"
                      [attr.inputmode]="billOfLadingData.number.inputmodeHouse"
                      [attr.pattern]="billOfLadingData.number.patternHouse"
                      [appMaskErrorTooltip]="billOfLadingData.number.maskMessageHouse"
                      [maxMaskLength]="billOfLadingData.number.maxMaskLength"
                      control-wrapper
                    />
                  </app-control-wrapper>
                </div>
              </div>

              <div class="form-row">
                <div class="col-12">
                  <p class="caption-hint">{{ 'ENTRY_SUMMARY.FILL_TOTAL_QUANTITY' | translate }}</p>
                </div>
                <div class="col-3">
                  <app-control-wrapper [control]="formGroup.get('quantity')" [errors]="errors">
                    <label for="quantity" control-label>{{ 'FORM_LABEL.TOTAL_QUANTITY' | translate }}</label>
                    <input
                      type="text"
                      [formControl]="formGroup.get('quantity')"
                      class="form-control"
                      id="quantity"
                      name="quantity"
                      [pattern]="decimalInputData.pattern"
                      [mask]="decimalInputData.mask"
                      [dropSpecialCharacters]="false"
                      [appMaskErrorTooltip]="decimalInputData.maskErrorTooltip"
                      inputmode="number"
                      control-wrapper
                    />
                  </app-control-wrapper>
                </div>
                <div class="col-6">
                  <app-control-wrapper [control]="formGroup.get('quantityUnit')" [errors]="errors">
                    <label for="unitOfMeasure" control-label>{{ 'FORM_LABEL.UNIT_OF_MEASURE' | translate }}</label>
                    <ng-select
                      id="unitOfMeasure"
                      class="custom"
                      [formControl]="formGroup.get('quantityUnit')"
                      [items]="quantityUnits"
                      [bindLabel]="'code'"
                      [bindValue]="'code'"
                      [searchFn]="searchQuantityUnit"
                      control-wrapper
                    >
                      <ng-template ng-label-tmp let-item="item">{{ item.code }} {{ item.description }}</ng-template>
                      <ng-template ng-option-tmp let-item="item">{{ item.code }} {{ item.description }}</ng-template>
                    </ng-select>
                  </app-control-wrapper>
                </div>
              </div>

              <div class="form-row" *ngIf="isImmediateTransfer">
                <div class="col-12">
                  <p class="caption-hint">
                    <span [innerHTML]="'ENTRY_SUMMARY.FILL_IMMEDIATE_TRANSFER' | translate"></span>

                    <app-hint-tooltip-icon [hint]="'ENTRY_SUMMARY.FILL_IMMEDIATE_TRANSFER' | translate"></app-hint-tooltip-icon>
                  </p>
                </div>
                <div class="col-6">
                  <app-control-wrapper [control]="formGroup.get('itNumber')" [errors]="errors.itNumber" [helper]="'FORM_HELPER.IT_NUMBER'">
                    <label for="itNumber" control-label>{{ 'FORM_LABEL.IT_NUMBER' | translate }}</label>
                    <input
                      type="text"
                      [formControl]="formGroup.get('itNumber')"
                      class="form-control"
                      id="itNumber"
                      name="itNumber"
                      [mask]="'A{11}'"
                      [appMaskErrorTooltip]="'MASK_ERROR.IT_NUMBER'"
                      [maxMaskLength]="11"
                      inputmode="text"
                      [placeholder]="'FORM_PLACEHOLDER.ENTER' | translate"
                      control-wrapper
                    />
                  </app-control-wrapper>
                </div>
                <div class="col-6">
                  <date-picker
                    [fc]="formGroup.get('itDate')"
                    [errors]="errors.itDate"
                    [helper]="'FORM_HELPER.IT_DATE'"
                    [label]="'FORM_LABEL.IT_DATE'"
                    [id]="'itDate'"
                    [name]="'itDate'"
                    [placeholder]="'FORM_PLACEHOLDER.CHOOSE'"
                    [minDate]="itMinDate"
                    [maxDate]="itMaxDate"
                  ></date-picker>
                </div>
              </div>
            </div>
            <div *ngIf="!isHasEsOcrRedesign" class="es-editable-bl-body-details-body-files">
              <document-upload-desktop
                [id]="'es-bl-' + shipment.id + 'documents'"
                [attachmentName]="'LABELS.BILL_OF_LADING'"
                [attachmentTypeName]="'LABELS.BILL_OF_LADING' | translate"
                [attachmentHint]="'LABELS.BILL_OF_LADING_HINT' | translate"
                [uploadedDocument]="blDoc"
                [canUploadDocuments]="isCanEdit"
                [canRemoveDocuments]="isCanEdit"
                [isRequired]="isRequiredDocument"
                (upload)="uploadDocumentCall(blDoc, $event)"
                (remove)="removeDocument(blDoc.id)"
                (stateUpdate)="onDocumentStateUpdate(blDoc.id, $event)"
              ></document-upload-desktop>
            </div>
          </div>
        </section>

        <div class="es-editable-bl-body-supplier">
          <es-details-entity-desktop
            icon="assets/icons/seller.svg"
            [title]="'INFO.SP.TITLE' | translate"
            [subTitle]="'INFO.SE.SUB_TITLE' | translate"
            [isShowEdit]="true"
            [entity]="shipment?.seller"
            (edit)="openEditSeller()"
          ></es-details-entity-desktop>
        </div>

        <section class="es-editable-bl-body-details inner-info" [ngClass]="{ 'is-valid': isTermsValid }">
          <header class="es-editable-bl-body-details-header">
            <div class="d-flex align-items-center justify-content-start">
              <div class="d-flex align-items-center">
                <span *ngIf="isTermsValid">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M10.0757 16.7728L6.17573 12.8728C5.94142 12.6385 5.94142 12.2586 6.17573 12.0243L7.02424 11.1757C7.25854 10.9414 7.63846 10.9414 7.87277 11.1757L10.5 13.8029L16.1272 8.17573C16.3615 7.94142 16.7415 7.94142 16.9758 8.17573L17.8243 9.02426C18.0586 9.25857 18.0586 9.63846 17.8243 9.87279L10.9243 16.7728C10.6899 17.0071 10.31 17.0071 10.0757 16.7728Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>

                <h5>
                  {{ 'LABELS.SHIPPING_TERMS_OF_SALE' | translate }}
                </h5>
              </div>

              <app-hint-tooltip-icon [hint]="'LABELS.SHIPPING_TERMS_OF_SALE' | translate"></app-hint-tooltip-icon>
            </div>
          </header>

          <div class="es-editable-bl-body-details-body">
            <div class="es-editable-bl-body-details-body-icon">
              <img src="assets/icons/globe.svg" [alt]="'LABELS.SHIPPING_TERMS_OF_SALE' | translate" />
            </div>

            <div class="es-editable-bl-body-details-body-form">
              <p>{{ 'LABELS.FREIGHT_PAYMENT_HINT' | translate }}</p>

              <div class="d-flex justify-content-between mb-4">
                <div
                  class="card card-step bl-type"
                  [ngClass]="{ 'is-selected': typeControl.value === enumChargesDistributionType.COLLECT }"
                >
                  <div class="card-header">
                    <app-radio-button
                      [control]="typeControl"
                      [id]="'bl-type-importer'"
                      [label]="'ENTITY.IMPORTER'"
                      [value]="enumChargesDistributionType.COLLECT"
                      [name]="'bl-type'"
                    ></app-radio-button>
                  </div>
                  <div class="card-body px-3">
                    <span>{{ 'LABELS.IMPORTER_HINT' | translate }}</span>
                  </div>
                </div>
                <div
                  class="card card-step bl-type"
                  [ngClass]="{ 'is-selected': typeControl.value === enumChargesDistributionType.PREPAID }"
                >
                  <div class="card-header">
                    <app-radio-button
                      [control]="typeControl"
                      [id]="'bl-type-exporter'"
                      [label]="'LABELS.EXPORTER'"
                      [value]="enumChargesDistributionType.PREPAID"
                      [name]="'bl-type'"
                    ></app-radio-button>
                  </div>
                  <div class="card-body px-3">
                    <span>{{ 'LABELS.EXPORTER_HINT' | translate }}</span>
                  </div>
                </div>
              </div>

              <ng-container *ngIf="typeControl.value === enumChargesDistributionType.COLLECT">
                <p class="mb-0" [innerHTML]="'LABELS.IMPORTER_DESCRIPTION' | translate"></p>
                <p>{{ 'LABELS.BL_TYPE_DESCRIPTION' | translate }}</p>

                <div class="form-row">
                  <div class="col-4">
                    <app-control-wrapper [control]="formGroup.get('freightCost')" [errors]="errors">
                      <label for="charges" control-label>
                        {{ 'FORM_LABEL.TOTAL_CHARGES' | translate }}
                        <i class="fa fa-asterisk form-required" aria-hidden="true"></i>
                      </label>
                      <div control-wrapper class="d-flex">
                        <input
                          id="charges"
                          class="form-control input-with-description-label"
                          type="text"
                          inputmode="numeric"
                          [formControl]="formGroup.get('freightCost')"
                          [pattern]="currencyInputMap.pattern"
                          [mask]="currencyInputMap.mask"
                          [thousandSeparator]="currencyInputMap.thousandSeparator"
                          [appMaskErrorTooltip]="currencyInputMap.maskErrorTooltip"
                        />
                        <div class="form-control input-with-description-label-box">{{ DEFAULT_CURRENCY_CODE }}</div>
                      </div>
                    </app-control-wrapper>
                  </div>
                </div>
              </ng-container>

              <ng-container *ngIf="typeControl.value === enumChargesDistributionType.PREPAID">
                <p class="mb-3" [innerHTML]="'LABELS.SHIPPING_TERMS_EXPORTER' | translate"></p>

                <div class="form-row">
                  <div class="col-4">
                    <app-control-wrapper
                      [control]="formGroup.get('freightCost')"
                      [errors]="errors"
                      helper="FORM_HELPER.AT_LEAST_ONE_PER_HTS_CODE"
                    >
                      <label for="freightCost" control-label>
                        {{ 'FORM_LABEL.FREIGHT_COST' | translate }}
                        <i class="fa fa-asterisk form-required" aria-hidden="true"></i>
                      </label>
                      <div control-wrapper class="d-flex">
                        <input
                          id="freightCost"
                          class="form-control input-with-description-label"
                          type="text"
                          inputmode="numeric"
                          [formControl]="formGroup.get('freightCost')"
                          [pattern]="currencyInputMap.pattern"
                          [mask]="currencyInputMap.mask"
                          [thousandSeparator]="currencyInputMap.thousandSeparator"
                          [appMaskErrorTooltip]="currencyInputMap.maskErrorTooltip"
                        />
                        <div class="form-control input-with-description-label-box">{{ DEFAULT_CURRENCY_CODE }}</div>
                      </div>
                    </app-control-wrapper>
                  </div>

                  <div class="col-4">
                    <app-control-wrapper [control]="formGroup.get('insurance')" [errors]="errors">
                      <label for="insurance" control-label>{{ 'FORM_LABEL.INSURANCE' | translate }}</label>
                      <div control-wrapper class="d-flex">
                        <input
                          id="insurance"
                          type="text"
                          class="form-control input-with-description-label"
                          formControlName="insurance"
                          inputmode="numeric"
                          [pattern]="currencyInputMap.pattern"
                          [mask]="currencyInputMap.mask"
                          [thousandSeparator]="currencyInputMap.thousandSeparator"
                          [appMaskErrorTooltip]="currencyInputMap.maskErrorTooltip"
                        />
                        <div class="form-control input-with-description-label-box">{{ DEFAULT_CURRENCY_CODE }}</div>
                      </div>
                    </app-control-wrapper>
                  </div>

                  <div class="col-4">
                    <app-control-wrapper class="w-100" [control]="formGroup.get('transportationCharge')" [errors]="errors">
                      <label for="transportationCharge" control-label>
                        {{ 'FORM_LABEL.TRANSPORTATION' | translate }}
                      </label>
                      <div control-wrapper class="form-row">
                        <div class="col-12">
                          <div class="d-flex">
                            <input
                              id="transportationCharge"
                              class="form-control input-with-description-label"
                              type="text"
                              formControlName="transportationCharge"
                              inputmode="numeric"
                              [pattern]="currencyInputMap.pattern"
                              [mask]="currencyInputMap.mask"
                              [thousandSeparator]="currencyInputMap.thousandSeparator"
                              [appMaskErrorTooltip]="currencyInputMap.maskErrorTooltip"
                            />
                            <div class="form-control input-with-description-label-box">{{ DEFAULT_CURRENCY_CODE }}</div>
                          </div>
                        </div>
                      </div>
                    </app-control-wrapper>
                  </div>
                </div>

                <p class="mb-0">{{ 'LABELS.BL_TYPE_DESCRIPTION' | translate }}</p>
              </ng-container>
            </div>
          </div>
        </section>
      </div>

      <footer class="d-flex justify-content-center mt-3 mb-3" *ngIf="isShowSaveBtn">
        <button type="button" class="btn btn-primary" (click)="saveBL.emit()">
          {{ 'BUTTON.SAVE' | translate }}
        </button>
      </footer>
    </section>

    <ng-template #entityItem let-entityItem="entityItem">
      <div class="es-entity-rows">
        <div class="row" *ngIf="isShowIdentifier">
          <div class="col-2 key">{{ 'LABELS.ID_NUMBER' | translate }}</div>
          <ng-container *ngIf="!getValidationText('entityIdentifier', entityItem); else missedIdentifier">
            <div class="col-10 value">
              <span *ngIf="entityItem?.entityIdentifierQualifier">
                {{ 'IDENTIFIER_QUALIFIER.' + entityItem.entityIdentifierQualifier | translate }}
              </span>
              <span *ngIf="entityItem?.entityIdentifier">{{ entityItem.entityIdentifier | identifier }}</span>
            </div>
          </ng-container>
          <ng-template #missedIdentifier>
            <div class="col-10 value text-error">
              {{
                getValidationText('entityIdentifier', entityItem) || getValidationText('entityIdentifierQualifier', entityItem) | translate
              }}
            </div>
          </ng-template>
        </div>

        <div class="row" *ngIf="entityItem?.dateOfBirth || entityItem?.entityIdentifierQualifier === enumIdentifierQualifier.SSN">
          <div class="col-2 key">{{ 'FORM_LABEL.DATEOFBIRTH' | translate }}</div>

          <ng-container *ngIf="!getValidationText('dateOfBirth', entityItem); else missedDateOfBirth">
            <div class="col-10 value">{{ entityItem.dateOfBirth | customDateByUtc }}</div>
          </ng-container>
          <ng-template #missedDateOfBirth>
            <div class="col-10 value text-error">{{ getValidationText('dateOfBirth', entityItem) | translate }}</div>
          </ng-template>
        </div>

        <div class="row">
          <div class="col-2 key">{{ 'LABELS.NAME' | translate }}</div>

          <ng-container *ngIf="!getValidationText('name', entityItem); else missedName">
            <div class="col-10 value">{{ entityItem?.name }}</div>
          </ng-container>
          <ng-template #missedName>
            <div class="col-10 value text-error">{{ getValidationText('name', entityItem) | translate }}</div>
          </ng-template>
        </div>

        <div class="row" *ngIf="isShowAddress">
          <div class="col-2 key">{{ 'LABELS.ADDRESS' | translate }}</div>

          <ng-container *ngIf="isValidShipEntityAddress(entityItem); else missedAdd">
            <div class="col-10 value" *ngIf="!getValidationText('address', entityItem)">
              <address-details [address]="entityItem.address"></address-details>
            </div>
            <div class="col-10 value text-error" *ngIf="getValidationText('address', entityItem)">
              {{ getValidationText('address', entityItem) | translate }}
            </div>
          </ng-container>
          <ng-template #missedAdd>
            <div class="col-10 value text-error">{{ 'BACK_ERROR.MISSING_ADDRESS_PARTS' | translate }}</div>
          </ng-template>
        </div>
      </div>
    </ng-template>
  `,
  styleUrls: ['./es-bill-of-lading-edit-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsBillOfLadingEditDesktopComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public shipment: ShipmentModel;
  @Input() public transportationMode: enumTransportationMode = enumTransportationMode.OCEAN;
  @Input() public formGroup: UntypedFormGroup;
  @Input() public isRequiredDocument: boolean = true;
  @Input() public isShowHeader: boolean = true;
  @Input() public isShowSaveBtn: boolean;
  @Input() public valToRunDetection: number;

  @Output() public addNewBl: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editBl: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editSeller: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editConsignee: EventEmitter<void> = new EventEmitter<void>();
  @Output() public saveBL: EventEmitter<void> = new EventEmitter<void>();

  public typeControl: UntypedFormControl;

  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;
  public decimalInputData: InputTypeMap = INPUT_TYPES_MAP.DECIMAL;
  public enumEntityModelCode = enumEntityModelCode;
  public enumChargesDistributionType = enumChargesDistributionType;
  public enumIdentifierQualifier = enumIdentifierQualifier;
  public currencyInputMap: InputTypeMap = INPUT_TYPES_MAP.CURRENCY;

  public blEnteredValue: number;
  public isBodyOpen: boolean = true;
  public currentLabelsData: { titleMaster: string; titleHouse: string; titleCarrier: string; titleNumber: string };

  public company: CompanyModel;
  public attachedDocuments: DocumentModel[] = [];
  public blDoc: DocumentModel;

  public isCanEdit: boolean = false;
  public isCanRemoveDocuments: boolean = false;
  public isShowDocuments: boolean = false;
  public isAir: boolean = false;
  public isRail: boolean = false;
  public isTruck: boolean;

  public isShowIdentifier: boolean;
  public isShowAddress: boolean = true;

  public isImmediateTransfer: boolean = false;
  public hasSingleExportData: boolean = false;
  public quantityUnits = [];

  public readonly errors: { [key: string]: any } = this.esCreateBlFormService.errorsData();
  public billOfLadingData: IBillOfLadingFormData;
  public initialForeignPortsItems: any[] = [];

  private componentSidebarRef: ComponentRef<any>;

  private readonly data: { [key: string]: IBillOfLadingFormData } = this.esCreateBlFormService.labelsData(true);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private entrySummaryService: EntrySummaryService,
    private esLabelsService: EsLabelsService,
    private changeDetector: ChangeDetectorRef,
    private documentsService: DocumentsService,
    private companiesService: CompaniesService,
    private translateService: TranslateService,
    private entrySummaryStateService: EntrySummaryStateService,
    private codeRepository: CodeRepository,
    private appService: AppService,
    private esStepsService: EsStepsService,
    private esCreateBlFormService: EsCreateBlFormService,
  ) {}

  public ngOnInit(): void {
    this.updateBlEnteredValue();
    this.updateLabelsData();

    this.getShipmentUnits();

    this.company = this.companiesService.getCompany();

    this.loadDocuments(false).pipe(take(1)).subscribe();

    this.typeControl = this.formGroup.get('chargesDistributionType') as UntypedFormControl;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.esCreateBlFormService.ctxOnChanges(changes, this);
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public onDocumentStateUpdate(documentId: number, state: enumDocumentState) {
    this.entrySummaryStateService.updateDocumentState(documentId, state);
    this.attachedDocuments = this.attachedDocuments.map((document) => (document.id === documentId ? { ...document, state } : document));
    this.prepareInvoiceDocuments();
  }

  public uploadDocument(document: DocumentCreateModel, file: File): Observable<any> {
    return this.entrySummaryStateService.uploadDocument(document, file, false).pipe(switchMap(() => this.loadDocuments(false)));
  }

  public uploadDocumentCall(document: DocumentCreateModel, file: File): void {
    this.uploadDocument(document, file).pipe(takeUntil(this.destroy$)).subscribe();
  }

  public removeDocument(documentId: number): void {
    this.entrySummaryStateService
      .removeDocument(documentId)
      .pipe(
        switchMap(() => this.loadDocuments(false)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public searchQuantityUnit(term: string, item: UnitModel) {
    term = term.toLowerCase().trim();
    return item.code.toLowerCase().indexOf(term) > -1 || item.description.toLowerCase().indexOf(term) > -1;
  }

  public get isHasEsOcrRedesign(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.esOcrRedesign);
  }

  public get itMinDate(): string {
    return this.entrySummaryStateService.itMinDate(
      this.entrySummary,
      this.hasSingleExportData ? this.formGroup.get('exportDate').value : null,
    );
  }

  public get itMaxDate(): string {
    return this.entrySummaryStateService.itMaxDate(this.entrySummary);
  }

  public getValidationText(fieldName: string, entity: EntityModel): string {
    return EntityValidationHelper.getEntityValidationText(entity, fieldName);
  }

  public isValidShipEntityAddress(entity: EntityModel): boolean {
    return EntityValidationHelper.isValidEntityAddress(entity?.address);
  }

  public openEditSeller(): void {
    const componentSidebarRef = this.appService.openSidebar(EsEntityUnifyDefaultDesktopComponent, {
      sidebarHeaderText: 'INFO.SP.EDIT',
    });

    componentSidebarRef.instance.formGroup = this.formGroup.get('seller');
    componentSidebarRef.instance.radioButton = this.formGroup.get(['seller', 'id']);
    componentSidebarRef.instance.info = DESC_ENTITY[enumEntityModelCode.SELLER];
    componentSidebarRef.instance.entityCode = enumEntityModelCode.SELLER;
    componentSidebarRef.instance.entityKeyName = 'seller';
    componentSidebarRef.instance.entity = this.shipment?.seller;

    componentSidebarRef.instance.entityDataSaved
      .pipe(
        takeUntil(this.destroy$),
        switchMap((seller: EntityModel) => {
          this.shipment.seller = seller;
          return this.updateOrCreateSeller(seller);
        }),
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary?.referenceNumber)),
      )
      .subscribe((entry: EntrySummaryModel) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.shipment = {
          ...this.shipment,
          seller: entry.shipments.find((shipment: ShipmentModel) => shipment.id === this.shipment?.id)?.seller || this.shipment?.seller,
        };

        this.appService.closeSidebar();
      });
  }

  public onEditDetails(formKey: string): void {
    this.componentSidebarRef = this.appService.openSidebar(EsEditShipmentDesktopComponent, {
      sidebarHeaderText: 'LABELS.EDIT_SHIPMENT_DETAILS',
    });

    this.componentSidebarRef.instance.entrySummary = this.entrySummary;
    this.componentSidebarRef.instance[formKey] = true;

    this.componentSidebarRef.instance.dataSaved.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.appService.closeSidebar();
      this.changeDetector.detectChanges();
    });
  }

  public get isCreatingInvoice(): boolean {
    return this.esStepsService.isCreatingInvoice;
  }

  public get isDetailsValid(): boolean {
    return (
      this.formGroup.get('masterBill').valid &&
      this.formGroup.get('houseBill').valid &&
      this.formGroup.get('itDate').valid &&
      this.formGroup.get('itNumber').valid &&
      this.formGroup.get('quantity').valid &&
      this.formGroup.get('quantityUnit').valid &&
      [enumDocumentState.UPLOADED, enumDocumentState.SCAN_PENDING].includes(this.blDoc?.state)
    );
  }

  public get isTermsValid(): boolean {
    return this.formGroup.get('freightCost').valid;
  }

  public get isWithVia(): boolean {
    return this.entrySummary?.portOfEntryCode !== this.entrySummary?.portOfUnladingCode;
  }

  private updateOrCreateSeller(seller: EntityModel): Observable<EntityModel> {
    if (!!seller?.id) {
      return this.entrySummaryStateService.updateEntity(seller.id, seller);
    } else {
      // create entity & update the shipment
      return this.entrySummaryStateService.createEntity(seller).pipe(
        switchMap((sellerRes) => {
          return this.entrySummaryStateService
            .updateShipment(this.shipment, { ...this.formGroup.value, sellerId: sellerRes.id })
            .pipe(switchMap(() => of(sellerRes)));
        }),
      );
    }
  }

  private updateBlEnteredValue(): void {
    this.blEnteredValue = 0;

    this.entrySummaryService
      .getShipmentEnteredValue(this.shipment)
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => (this.blEnteredValue = value));
  }

  private updateLabelsData() {
    this.currentLabelsData = this.esLabelsService.getLabelsData(this.transportationMode);
  }

  private loadDocuments(isShowLoader: boolean) {
    return this.entrySummaryStateService.updateDocuments(isShowLoader).pipe(
      takeUntil(this.destroy$),
      tap((documents) => {
        this.blDoc = null;
        this.attachedDocuments = documents;
        this.prepareInvoiceDocuments();

        this.changeDetector.detectChanges();
      }),
    );
  }

  private findAttachedDocument() {
    return this.attachedDocuments.find(
      (document) =>
        document.relatedEntityType === enumDocumentRelatedEntityType.SHIPMENT &&
        document.type === enumDocumentType.BILL_OF_LADING &&
        document.relatedEntityId === this.shipment.id,
    );
  }

  private prepareInvoiceDocuments(): void {
    const blDoc = this.findAttachedDocument() || {
      relatedEntityId: this.shipment.id,
      type: enumDocumentType.BILL_OF_LADING,
      relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
      rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
      rootEntityId: this.entrySummary?.id,
      fileName: null,
      url: null,
      createdDate: null,
      id: null,
      state: enumDocumentState.CREATED,
    };

    this.blDoc = this.mergeDocument(this.blDoc, blDoc);
  }

  private mergeDocument(existedDocument: DocumentModel, newDocument: DocumentModel): DocumentModel {
    if (!existedDocument) {
      return newDocument;
    }
    Object.assign(existedDocument, newDocument);
    return existedDocument;
  }

  private updateErrorsCode(transportationMode: enumTransportationMode) {
    const billMessage = transportationMode === enumTransportationMode.OCEAN ? 'FORM_ERROR.SAME_BL_OCEAN' : 'FORM_ERROR.SAME_BL_AIR';
    if (!!this.errors?.masterBillNumber) {
      this.errors.masterBillNumber.sameAsHouseBillNumber = billMessage;
    }
    if (!!this.errors?.houseBillNumber) {
      this.errors.masterBillNumber.sameAsMasterBillNumber = billMessage;
    }
  }

  private getShipmentUnits() {
    this.codeRepository
      .getShipmentUnits()
      .pipe(takeUntil(this.destroy$))
      .subscribe((quantityUnits) => {
        this.quantityUnits = quantityUnits;
      });
  }
}
